import { Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import type { CSSProperties } from "react";

interface ErrorMapEntry {
	title: string;
	message: string;
	actions?: { label: string; to: string }[];
}

const ERROR_MAP: Record<string | number, ErrorMapEntry> = {
	91: {
		// 101
		title: "Bills Duplicate found in IRD",
		message:
			"Some bills are in an error state. Go to the Error Fix page to verify and sync them with IRD servers.",
		actions: [{ label: "Go to Error Fix", to: "/error-fix" }],
	},
	92: {
		// 500
		title: "IRD Servers Are Offline",
		message:
			"We detected that IRD servers are currently unavailable. Your actions will be queued until the servers are back online.",
	},
	93: {
		// 100
		title: "Account Verification Failed",
		message:
			"Your IRD account credentials could not be verified. Please contact an admin user. Bills cannot be generated until this is resolved.",
	},
	94: {
		// 102, 103, 104
		title: "System Processing Error",
		message:
			"A technical failure occurred. Please contact the service provider for further assistance.",
	},
	// Client-side (not a BE response code) — the unsynced-bills list itself
	// failed to load on /error-fix. String key, deliberately not a number,
	// so it can't be confused with a real BE `code` from the 91-94 set above.
	"error-fix-load-failed": {
		title: "Couldn't Load Unsynced Bills",
		message:
			"We couldn't reach the bill server to load your unsynced bills list. Please try again in a moment.",
		actions: [{ label: "Try Again", to: "/error-fix" }],
	},
	default: {
		title: "Unexpected Error",
		message:
			"Something went wrong. Please try again, or contact support if the issue continues.",
	},
};

export default function InternalErrorPage() {
	const { state } = useLocation();
	const navigate = useNavigate();
	const code = state?.code;

	if (!code) {
		// If user refreshes or comes directly without state
		return <Navigate to="/" replace />;
	}
	const error = ERROR_MAP[code] || ERROR_MAP.default;

	return (
		<div style={styles.container}>
			<div style={styles.card}>
				<h1 style={styles.title}>{error.title}</h1>
				<p style={styles.message}>{error.message}</p>

				{error.actions?.map((a) => (
					<Link key={a.label} to={a.to} style={styles.button}>
						{a.label}
					</Link>
				))}

				<button
					onClick={() => {
						if (code === 93) {
							navigate("/");
						} else {
							navigate(-1);
						}
					}}
					style={styles.secondary}
				>
					Go Back
				</button>
			</div>
		</div>
	);
}

const styles: Record<string, CSSProperties> = {
	container: {
		minHeight: "100vh",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: "linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)",
	},
	card: {
		background: "#fff",
		padding: "32px",
		borderRadius: "16px",
		maxWidth: "560px",
		width: "100%",
		boxShadow: "0 20px 40px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.05)",
	},
	title: {
		marginBottom: "10px",
	},
	message: {
		color: "#555",
		marginBottom: "24px",
		lineHeight: 1.6,
	},
	button: {
		display: "inline-block",
		padding: "10px 16px",
		borderRadius: "10px",
		background: "#2563eb",
		color: "#fff",
		textDecoration: "none",
		marginRight: "10px",
	},
	secondary: {
		display: "inline-block",
		marginTop: "10px",
		color: "#555",
		textDecoration: "underline",
		outline: 0,
		border: 0,
		background: "transparent",
		cursor: "pointer",
	},
};
